import axios from 'axios'
import { useEffect, useMemo, useState } from 'react'

import store from 'Store'
import { setOnline, setUnreliable } from 'Store/auth/networkSlice'
import { apiError, authorization } from 'Utils/alerts'

const { REACT_APP_URL } = process.env

const BaseService = axios.create({
    baseURL: `${REACT_APP_URL}/api`,
    timeout: 100000,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
    },
})

// --- Auto-retry transient failures ---------------------------------------
// On a live server, a single Network Error / 502 / dropped connection used to
// fire an immediate toast. Most of these are momentary: a reverse-proxy
// restart, a wifi reconnect, a brief DNS hiccup. We retry up to MAX_RETRIES
// times with exponential backoff before surfacing the error.
const MAX_RETRIES = 2
const RETRY_STATUSES = new Set([401, 408, 425, 429, 500, 502, 503, 504])

// True when axios couldn't reach the server at all — no response headers,
// no HTTP status. This is the reliable "we are offline" signal, because
// navigator.onLine lies (returns true on captive portals, LAN-only wifi,
// broken uplinks, etc.).
const isNoResponseError = err => {
    if (axios.isCancel(err)) return false
    if (err.response) return false
    return err.message === 'Network Error' || err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK'
}

// True for anything worth retrying — no-response errors PLUS 5xx-family
// statuses that typically clear on a retry.
const isTransientError = err => {
    if (isNoResponseError(err)) return true
    const status = err.response?.status
    return status != null && RETRY_STATUSES.has(status)
}

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

// --- Network status signals ----------------------------------------------
// Drives the NetworkBanner via Redux.
//
//   OFFLINE  — one confirmed no-response failure. Set immediately so the user
//              sees the red banner the moment we can't reach the server (this
//              is more reliable than navigator.onLine, which lies).
//   UNRELIABLE — two consecutive 5xx failures after their retries were exhausted.
//                Server is reachable but degraded.
//
// Any 2xx response clears both flags.
let consecutiveTransientFailures = 0

const markSuccess = () => {
    if (consecutiveTransientFailures !== 0) consecutiveTransientFailures = 0
    const s = store.getState().network
    if (s?.isUnreliable) store.dispatch(setUnreliable(false))
    if (s?.isOnline === false) store.dispatch(setOnline(true))
}

const markNoResponseFailure = () => {
    if (store.getState().network?.isOnline !== false) {
        store.dispatch(setOnline(false))
    }
}

const markTransientFailure = () => {
    consecutiveTransientFailures += 1
    if (consecutiveTransientFailures >= 2 && !store.getState().network?.isUnreliable) {
        store.dispatch(setUnreliable(true))
    }
}

export function useAxiosLoader() {
    const [count, setCount] = useState(0)

    const interceptors = useMemo(() => {
        const inc = loader => setCount(c => (loader ? c + 1 : c))
        const dec = loader => setCount(c => (loader ? c - 1 : c))
        return {
            request: req => {
                // Don't double-increment when this is a retry of an already-counted request.
                if (!req.__isRetry) inc(req.loader ?? true)
                authorization(req)
                return req
            },
            response: res => {
                markSuccess()
                dec(res.config.loader ?? true)
                return res
            },
            error: async err => {
                const config = err?.config

                // Retry path — keep the loader counter untouched so the spinner
                // stays visible across the whole retry chain.
                if (config && isTransientError(err) && (config.__retryCount || 0) < MAX_RETRIES) {
                    config.__isRetry = true
                    config.__retryCount = (config.__retryCount || 0) + 1
                    await sleep(300 * 2 ** (config.__retryCount - 1)) // 300ms, 600ms
                    return BaseService(config)
                }

                // Terminal failure path. Prefer the strongest signal:
                //   no response at all → OFFLINE (fires on the first failure)
                //   otherwise transient (5xx) → UNRELIABLE after 2 in a row
                if (isNoResponseError(err)) {
                    markNoResponseFailure()
                } else if (isTransientError(err)) {
                    markTransientFailure()
                }
                if (config) dec(config.loader ?? true)
                if (!axios.isCancel(err)) apiError(err)
                return Promise.reject(err)
            },
        }
    }, [])

    useEffect(() => {
        const reqInterceptor = BaseService.interceptors.request.use(interceptors.request, interceptors.error)
        const resInterceptor = BaseService.interceptors.response.use(interceptors.response, interceptors.error)
        return () => {
            BaseService.interceptors.request.eject(reqInterceptor)
            BaseService.interceptors.response.eject(resInterceptor)
        }
    }, [interceptors])

    return count > 0
}

export default BaseService
